/**
 * Lightweight UI sound cues, synthesized with the Web Audio API so no audio
 * assets need to load. Every function fails silently when the browser blocks
 * audio (e.g. no prior user interaction) — sound is an enhancement only.
 */

type AudioContextCtor = typeof AudioContext

let audioContext: AudioContext | null = null

function getAudioContext(): AudioContext | null {
  if (typeof window === 'undefined') return null
  const Ctor: AudioContextCtor | undefined =
    window.AudioContext ??
    (window as unknown as { webkitAudioContext?: AudioContextCtor }).webkitAudioContext
  if (!Ctor) return null
  if (!audioContext) {
    try {
      audioContext = new Ctor()
    } catch {
      return null
    }
  }
  return audioContext
}

function playTone(
  ctx: AudioContext,
  frequency: number,
  start: number,
  duration: number,
  peak: number,
) {
  const oscillator = ctx.createOscillator()
  const gain = ctx.createGain()
  oscillator.type = 'sine'
  oscillator.frequency.setValueAtTime(frequency, start)
  gain.gain.setValueAtTime(0.0001, start)
  gain.gain.exponentialRampToValueAtTime(peak, start + 0.015)
  gain.gain.exponentialRampToValueAtTime(0.0001, start + duration)
  oscillator.connect(gain)
  gain.connect(ctx.destination)
  oscillator.start(start)
  oscillator.stop(start + duration + 0.05)
}

/** A soft two-note "ready" chime. Resolves false if audio is unavailable. */
export async function playReadyChime(): Promise<boolean> {
  const ctx = getAudioContext()
  if (!ctx) return false
  try {
    if (ctx.state === 'suspended') await ctx.resume()
  } catch {
    return false
  }
  if (ctx.state !== 'running') return false
  const t = ctx.currentTime + 0.02
  playTone(ctx, 659.25, t, 0.5, 0.08) // E5
  playTone(ctx, 987.77, t + 0.12, 0.75, 0.07) // B5
  playTone(ctx, 1318.51, t + 0.12, 0.6, 0.018) // E6 shimmer
  return true
}

/** Speaks a short phrase with the system voice, if supported. */
export function speakPhrase(text: string): void {
  if (typeof window === 'undefined' || !('speechSynthesis' in window)) return
  try {
    const synth = window.speechSynthesis
    synth.cancel()
    const utterance = new SpeechSynthesisUtterance(text)
    utterance.lang = 'en-US'
    utterance.rate = 1
    utterance.pitch = 1
    utterance.volume = 0.85
    const voices = synth.getVoices()
    const preferred =
      voices.find((v) => v.lang.startsWith('en') && /samantha|google us|female|aria|jenny/i.test(v.name)) ??
      voices.find((v) => v.lang.startsWith('en'))
    if (preferred) utterance.voice = preferred
    synth.speak(utterance)
  } catch {
    /* speech unavailable — the chime alone is enough */
  }
}

/** Chime followed by a spoken "Your analysis is ready." */
export async function playAnalysisReadyCue(): Promise<void> {
  const chimed = await playReadyChime()
  if (!chimed) return
  window.setTimeout(() => speakPhrase('Your analysis is ready.'), 450)
}
